// Admin console data layer (Phase 6) — the staff directory with its role and
// is_active writes, the caseload check that gates offboarding, the audit-events
// reader, and the RLS-enforcement probe behind the honest access-control copy.
//
// Ported from legacy v20_Index.html:
//   loadRoleEditor / saveUserRole (~4775-4805) — the role write, byte-identical
//   logUserActivity              (~4498)      — the audit line for admin actions
//   loadAuditTrail/renderAuditTrail (~4826-4870) — the audit viewer's read+filter
//
// ── THE THREE RULES THIS MODULE EXISTS TO HOLD ────────────────────────────
//
// 1. A ROLE CHANGE IS AN ACCESS CHANGE. app_users.role is read by TWO different
//    gates with DIFFERENT regexes: isAdmin() (/admin|programmes/i) opens the
//    admin console, and canAssign() (/admin|programmes|coordinator/i) grants
//    assignment powers. Separately assignableUsers() EXCLUDES admins, so
//    promoting a nurse to admin removes them from every ownership picker. The
//    consequence copy is generated HERE (roleConsequence) from those same
//    predicates rather than typed into the view, so it cannot drift from the
//    gates it describes.
//
// 2. NOTHING EVER SILENTLY UN-OWNS A PATIENT. Deactivating a user does NOT
//    touch cases.assigned_to. A patient assigned to an inactive account appears
//    in no active nurse's 'mine' queue, so the admin is shown the caseload and
//    told to reassign FIRST — but the app will not null the column on their
//    behalf. Losing the ownership record is worse than an awkward queue.
//
// 3. WE DO NOT CLAIM SERVER-SIDE ENFORCEMENT WE HAVE NOT VERIFIED. Deactivation
//    is client-authoritative until migration 20260719120000 is applied to THIS
//    database. probeRlsEnforcement() asks the database instead of asserting
//    (see RPCS.IS_ACTIVE_STAFF), and every copy path has a true form for both
//    outcomes AND for "we could not check".

import { getClient, ok, fail } from '../db/client.js';
import { TABLES, RPCS, isClosedCase } from '../db/contract.js';
import { logAudit, AUDIT_EVENT } from './audit.js';
import { isAdmin, canAssign } from '../auth.js';

// ─── Role vocabulary ───────────────────────────────────────────────────────
// app_users.role is free-form TEXT in the database. v_20 offered exactly these
// three and preserved any other existing value as its own option so an
// unrecognised legacy role could never be silently rewritten by opening a
// dropdown. Same rule here.
export const ROLES = Object.freeze(['nurse', 'coordinator', 'admin']);

/**
 * The <option> value list for one user: the canonical roles plus their CURRENT
 * role when it is something else (order preserved, no duplicates). Pure.
 * @param {string|null|undefined} current the user's app_users.role
 * @returns {string[]}
 */
export function roleOptionsFor(current) {
  const cur = String(current || 'nurse');
  return [...new Set([...ROLES, cur])];
}

/**
 * What a role MEANS for access, derived from the live gate predicates rather
 * than restated. Pure — unit-tested against auth.js so the sentence and the
 * gate can never disagree.
 * @param {string|null|undefined} role
 * @returns {{admin: boolean, assign: boolean, ownsPatients: boolean, text: string}}
 */
export function roleConsequence(role) {
  const admin = isAdmin(role);
  const assign = canAssign(role);
  // assignableUsers() in data/users.js excludes admin/programmes — the same
  // regex, evaluated here so the two cannot drift.
  const ownsPatients = !/admin|programmes/i.test(String(role || ''));
  const parts = [];
  parts.push(admin ? 'Manages the programme (Admin console, roles, audit).' : 'No access to the Admin console.');
  parts.push(assign ? 'Can assign patients to nurses.' : 'Can pick up unassigned patients, but cannot bulk-assign.');
  parts.push(ownsPatients ? 'Can own patients.' : 'Cannot own patients — removed from every assignment picker.');
  return { admin, assign, ownsPatients, text: parts.join(' ') };
}

/**
 * Sort staff for display: active first, then by name (legacy ordered by
 * full_name only; inactive accounts sink so the working team reads first).
 * Pure.
 * @param {any[]} users
 * @returns {any[]}
 */
export function sortTeam(users) {
  return (users || []).slice().sort((a, b) => {
    const aa = a && a.is_active === false ? 1 : 0;
    const bb = b && b.is_active === false ? 1 : 0;
    if (aa !== bb) return aa - bb;
    return String((a && a.full_name) || '').localeCompare(String((b && b.full_name) || ''));
  });
}

// Optional app_users columns the team list SHOWS IF PRESENT. They are not in
// the verified schema (v_20 only ever selected user_id/full_name/role/
// is_active), so the loader selects '*' and the view renders a field only when
// the column actually came back. Inventing an `email` column in the fixtures to
// make a nicer screenshot is exactly the P4 mistake — a fixture shaped like the
// UI's wishes hides the fact that production has nothing to show.
export const EMAIL_KEYS = Object.freeze(['email', 'user_email', 'contact_email']);
export const LAST_SEEN_KEYS = Object.freeze(['last_seen_at', 'last_sign_in_at', 'last_login_at']);

/**
 * First present, non-empty value among candidate keys (pure).
 * @param {any} row
 * @param {readonly string[]} keys
 * @returns {string|null}
 */
export function firstField(row, keys) {
  for (const k of keys) {
    const v = row && row[k];
    if (v !== undefined && v !== null && String(v) !== '') return String(v);
  }
  return null;
}

// ─── Reads ─────────────────────────────────────────────────────────────────

/**
 * Load the full staff directory for the admin table. Selects '*' deliberately:
 * this is the one admin-only surface that wants every column app_users happens
 * to carry (email / last-seen if the deployment has them), and naming columns
 * that may not exist would fail the whole read.
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function loadTeam(deps) {
  const client = (deps && deps.client) || getClient();
  try {
    const { data, error } = await client.from(TABLES.APP_USERS).select('*').order('full_name');
    if (error) return fail(error);
    return ok(sortTeam(data || []));
  } catch (e) {
    return fail(e);
  }
}

/**
 * How many LIVE patients a user owns. Counts client-side through isClosedCase()
 * rather than with a server-side count, because "closed" is spread across
 * program_status / current_stage / archived_at and a naive `count(*)` would
 * warn the admin about 40 patients when 34 of them are closed cases nobody
 * needs to reassign. The read is narrow (5 columns, one owner).
 * @param {string} uid
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>} data = {open, total}
 */
export async function caseloadFor(uid, deps) {
  const client = (deps && deps.client) || getClient();
  try {
    const { data, error } = await client
      .from(TABLES.CASES)
      // `is_archived` is deliberately NOT selected. It does not exist on the
      // production database (which carries archived_at only), and PostgREST
      // errors on an unknown column rather than ignoring it — naming it here
      // would have broken the offboarding caseload check the moment this app
      // pointed at production. Nothing is lost: isClosedCase reads
      // `archived_at || is_archived`, archived_at IS selected, and the client
      // derives is_archived from it in normalizeCase anyway.
      .select('case_id,program_status,current_stage,archived_at')
      .eq('assigned_to', uid);
    if (error) return fail(error);
    const rows = data || [];
    return ok({ open: rows.filter((r) => !isClosedCase(r)).length, total: rows.length });
  } catch (e) {
    return fail(e);
  }
}

/**
 * Is deactivation enforced server-side on THIS database? Calls the helper that
 * migration 20260719120000 creates. Three honest outcomes, never two:
 *   {checked:true,  applied:true }  — the function exists and answered
 *   {checked:true,  applied:false}  — the function is absent (PGRST202 / 42883)
 *   {checked:false, applied:false}  — the probe itself failed (offline, denied):
 *                                     we DO NOT KNOW, and the UI must say so.
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function probeRlsEnforcement(deps) {
  const client = (deps && deps.client) || getClient();
  try {
    const { error } = await client.rpc(RPCS.IS_ACTIVE_STAFF);
    if (!error) return ok({ checked: true, applied: true });
    if (isMissingFunction(error)) return ok({ checked: true, applied: false });
    return ok({ checked: false, applied: false, error: error });
  } catch (e) {
    return ok({ checked: false, applied: false, error: e });
  }
}

/**
 * Does this driver error mean "that function does not exist"? PostgREST reports
 * an unknown RPC as PGRST202 (schema-cache miss); PostgreSQL itself uses 42883.
 * Anything else (401/permission/network) is NOT evidence of absence. Pure.
 * @param {any} error
 * @returns {boolean}
 */
export function isMissingFunction(error) {
  const code = String((error && error.code) || '');
  if (code === 'PGRST202' || code === '42883') return true;
  const msg = String((error && error.message) || '');
  return /could not find the function|does not exist/i.test(msg) && /function|rpc/i.test(msg);
}

// ─── Audit events ──────────────────────────────────────────────────────────

// The audit read is CAPPED, not exhaustive: audit_events is the largest table in
// the system and grows forever, so a fetchAll() of it would pull the whole
// organisation's history into the browser to show 50 rows. We take the most
// recent AUDIT_PAGE rows (server-ordered occurred_at desc, filters pushed to the
// server) and TELL the admin the list is capped — a truncated list that claims
// to be complete is the R-013 lie in a different table.
export const AUDIT_PAGE = 200;
export const AUDIT_MAX = 1000;

// The event_type filter vocabulary (legacy #audit-type option list, verbatim
// order). 'all' is handled by the caller.
export const AUDIT_TYPES = Object.freeze([
  Object.freeze({ v: 'consent_submitted', l: 'Consent submitted' }),
  Object.freeze({ v: 'consent_promoted', l: 'Consent promoted' }),
  Object.freeze({ v: 'consent_status_changed', l: 'Consent status changed' }),
  Object.freeze({ v: 'case_created', l: 'Case created' }),
  Object.freeze({ v: 'case_assigned', l: 'Case assigned' }),
  Object.freeze({ v: 'case_stage_changed', l: 'Stage changed' }),
  Object.freeze({ v: 'case_archived', l: 'Case archived' }),
  Object.freeze({ v: 'task_completed', l: 'Task completed' }),
  Object.freeze({ v: 'adverse_event_reported', l: 'AE reported' }),
  Object.freeze({ v: 'prescription_updated', l: 'Prescription updated' }),
  Object.freeze({ v: 'expense_created', l: 'Expense added' }),
  Object.freeze({ v: 'expense_deleted', l: 'Expense voided' }),
  Object.freeze({ v: 'data_changed', l: 'Admin / data change' }),
]);

/**
 * Human label for an event_type — known ones from AUDIT_TYPES, anything else
 * de-underscored (a new server-side event must still read as words). Pure.
 * @param {string} v
 * @returns {string}
 */
export function eventLabel(v) {
  const hit = AUDIT_TYPES.find((t) => t.v === v);
  return hit ? hit.l : String(v || '—').replace(/_/g, ' ');
}

/**
 * Turn a 'YYYY-MM-DD' day into the inclusive ISO bound for a timestamptz
 * column. `to` must cover the WHOLE day — filtering `occurred_at <= '2026-07-20'`
 * compares against midnight and silently drops everything that happened that
 * day, which is exactly the day an admin investigating an incident cares about.
 *
 * The day is read in the VIEWER'S timezone, because `<input type="date">` means
 * a local day to the person who picked it. Building UTC bounds instead cut the
 * first three hours off every Kenyan (EAT, UTC+3) day: an event logged at 01:30
 * EAT on the 20th is 2026-07-19T22:30Z, so a From=To=20th filter dropped it and
 * wrongly swept in 00:00–02:59 EAT of the 21st. Deterministic for a given zone;
 * no clock read, so still safe to test.
 * @param {string} day 'YYYY-MM-DD'
 * @param {'from'|'to'} edge
 * @returns {string|null}
 */
export function dayBound(day, edge) {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  // No 'Z' suffix — the Date constructor parses a date-TIME string as LOCAL.
  const d = new Date(day + (edge === 'to' ? 'T23:59:59.999' : 'T00:00:00.000'));
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Read audit events, newest first, with the filters pushed SERVER-side (a
 * client-side filter over a capped page shows "no results" for events that are
 * simply below the cap).
 * @param {{type?: string, actor?: string, from?: string, to?: string, limit?: number}} [filters]
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>} data = {rows, limit, capped}
 */
export async function loadAuditEvents(filters, deps) {
  const f = filters || {};
  const client = (deps && deps.client) || getClient();
  const limit = Math.min(Math.max(Number(f.limit) || AUDIT_PAGE, 1), AUDIT_MAX);
  try {
    let q = client.from(TABLES.AUDIT_EVENTS).select('*').order('occurred_at', { ascending: false });
    if (f.type && f.type !== 'all') q = q.eq('event_type', f.type);
    if (f.actor && f.actor !== 'all') q = q.eq('actor_user_id', f.actor);
    const lo = dayBound(f.from || '', 'from');
    const hi = dayBound(f.to || '', 'to');
    if (lo) q = q.gte('occurred_at', lo);
    if (hi) q = q.lte('occurred_at', hi);
    const { data, error } = await q.range(0, limit - 1);
    if (error) return fail(error);
    const rows = data || [];
    // `capped` is only knowable as "we got exactly as many as we asked for" —
    // reported as a maybe, never as a count we did not measure.
    return ok({ rows, limit, capped: rows.length >= limit });
  } catch (e) {
    return fail(e);
  }
}

/**
 * Client-side free-text narrowing WITHIN the loaded page (legacy
 * renderAuditTrail search). Pure. Deliberately searches only summary +
 * event_type: `details` can hold clinical notes, and a substring hit inside a
 * note the admin cannot see in the row is a confusing result, not a useful one.
 * @param {any[]} rows
 * @param {string} q
 * @returns {any[]}
 */
export function searchAudit(rows, q) {
  const s = String(q || '').trim().toLowerCase();
  if (!s) return rows || [];
  return (rows || []).filter((e) => {
    const hay = String((e && e.summary) || '') + ' ' + String((e && e.event_type) || '');
    return hay.toLowerCase().includes(s);
  });
}

// The `details` keys the viewer renders as chips. An ALLOW-LIST, not a
// blocklist: details is a free-form JSONB column that server-side jobs also
// write, so rendering every key would eventually surface a payload nobody
// reviewed onto an admin screen. Bookkeeping keys (by/by_id) are omitted
// because the row already shows "by {name}".
export const DETAIL_KEYS = Object.freeze([
  'kind', 'from', 'to', 'stage', 'from_stage', 'to_stage', 'reason', 'status',
  'category', 'amount', 'self_pickup', 'at_promote', 'user',
]);

/**
 * The chips for one audit row: allow-listed keys with primitive values, in
 * DETAIL_KEYS order. Objects/arrays are dropped rather than JSON-stringified —
 * a nested object is where an un-reviewed payload hides. Pure.
 * @param {any} details
 * @returns {Array<{k: string, v: string}>}
 */
export function detailChips(details) {
  if (!details || typeof details !== 'object') return [];
  /** @type {Array<{k: string, v: string}>} */
  const out = [];
  for (const k of DETAIL_KEYS) {
    const v = details[k];
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'object') continue;
    out.push({ k: k.replace(/_/g, ' '), v: String(v) });
  }
  return out;
}

// ─── Writes (admin-only) ───────────────────────────────────────────────────

/**
 * The one guard both admin writes run through. Returns null when allowed, else
 * the refusal copy. Coordinators pass canAssign() but must NOT reach these —
 * the ability to route patients is not the ability to grant admin. Pure.
 * @param {string|null|undefined} role the SIGNED-IN user's role
 * @returns {string|null}
 */
export function adminWriteGuard(role) {
  return isAdmin(role) ? null : 'Only admins can change team roles or access';
}

/**
 * Refuse a write that would leave the deployment with NO active administrator.
 *
 * adminWriteGuard only asks "is the person clicking an admin?" — which the sole
 * admin demoting themselves passes trivially, and which the server-side policy
 * (`using (is_admin()) with check (is_admin())`) also passes, because they are
 * still an admin at UPDATE time. The write therefore succeeds, and on the next
 * page load visibleRoutes() drops the MANAGE tier and there is no in-app route
 * back: recovery needs the Supabase SQL editor. Worse, it takes the MFA
 * recovery story with it — MFA_RECOVERY_COPY promises "another administrator
 * must remove your factor", and after this there is no other administrator.
 *
 * Counts the CURRENT team, not the signed-in user, so it is equally correct for
 * demoting somebody else who happens to be the last admin. Pure and tested.
 *
 * @param {string} uid the user being changed
 * @param {{role?: string|null, active?: boolean}} next what they are becoming;
 *        `role` for a role change, `active:false` for a deactivation
 * @param {Array<any>|null|undefined} team loadTeam() rows
 * @returns {string|null} refusal copy, or null when allowed
 */
export function lastAdminGuard(uid, next, team) {
  // No team list => no evidence. Refusing on no evidence would brick the screen
  // whenever loadTeam() failed; the caller must pass a loaded list.
  if (!Array.isArray(team) || !team.length) return null;
  const admins = team.filter((u) => u && u.is_active !== false && isAdmin(u.role));
  // Is the target one of the current active admins? If not, nothing they become
  // can reduce the count.
  if (!admins.some((u) => String(u.user_id) === String(uid))) return null;
  if (admins.length > 1) return null;
  const staysAdmin = next && next.role !== undefined ? isAdmin(next.role) : true;
  const staysActive = next && next.active !== undefined ? !!next.active : true;
  if (staysAdmin && staysActive) return null;
  return (
    'This is the only active administrator, so this change would leave the app with no one who can manage ' +
    'roles, restore access, or clear a locked-out colleague’s two-step verification — and no way back in ' +
    'except the Supabase dashboard. Give somebody else the admin role first, then make this change.'
  );
}

/**
 * Change a user's role (legacy saveUserRole — same table, same single-column
 * update, same audit line). Refuses a no-op so the audit trail never fills with
 * "changed role for Jane to nurse" when nothing changed.
 * @param {string} uid app_users.user_id
 * @param {string} name full_name (baked into the audit summary — survives the row)
 * @param {string} newRole
 * @param {string|null} prevRole
 * @param {{client?: any, role?: string, session?: any, team?: Array<any>|null}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function saveUserRole(uid, name, newRole, prevRole, deps) {
  const denied = adminWriteGuard(deps && deps.role);
  if (denied) return fail(new Error(denied), 'FORBIDDEN');
  if (!newRole || newRole === prevRole) return fail(null, 'NO_CHANGE');
  const orphan = lastAdminGuard(uid, { role: newRole }, deps && deps.team);
  if (orphan) return fail(new Error(orphan), 'LAST_ADMIN');
  const client = (deps && deps.client) || getClient();
  try {
    const { error } = await client.from(TABLES.APP_USERS).update({ role: newRole }).eq('user_id', uid);
    if (error) return fail(error);
  } catch (e) {
    return fail(e);
  }
  // Legacy summary text, verbatim — the two apps write one readable trail.
  await logAudit(
    AUDIT_EVENT.DATA_CHANGED,
    'changed role for ' + name + ' to ' + newRole,
    null,
    { user: uid, from: prevRole || null, to: newRole },
    deps
  );
  return ok({ uid, role: newRole });
}

/**
 * Deactivate / reactivate a staff account (R-078 interim offboarding).
 *
 * DELIBERATELY DOES NOT TOUCH cases.assigned_to. See rule 2 at the top of this
 * file: an inactive owner is a visible problem the admin can fix; a nulled
 * assigned_to is a lost fact nobody can reconstruct.
 *
 * @param {string} uid
 * @param {string} name
 * @param {boolean} active target state
 * @param {{client?: any, role?: string, session?: any, team?: Array<any>|null}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function setUserActive(uid, name, active, deps) {
  const denied = adminWriteGuard(deps && deps.role);
  if (denied) return fail(new Error(denied), 'FORBIDDEN');
  const orphan = lastAdminGuard(uid, { active: !!active }, deps && deps.team);
  if (orphan) return fail(new Error(orphan), 'LAST_ADMIN');
  const client = (deps && deps.client) || getClient();
  try {
    const { error } = await client.from(TABLES.APP_USERS).update({ is_active: !!active }).eq('user_id', uid);
    if (error) return fail(error);
  } catch (e) {
    return fail(e);
  }
  await logAudit(
    AUDIT_EVENT.DATA_CHANGED,
    (active ? 'reactivated app access for ' : 'deactivated app access for ') + name,
    null,
    { user: uid, to: active ? 'active' : 'inactive' },
    deps
  );
  return ok({ uid, is_active: !!active });
}
